import { useEffect, useState } from 'react'
import api from '../../services/api'
import { useTheme } from '../../hooks/useTheme.jsx'
import { formatDate, formatDateTime } from '../../utils/helpers'

/**
 * Every file in the bucket, and whether anything still uses it. Super Admin only.
 *
 * Nothing on this page deletes. "Move to trash" puts a file aside and it is removed for good 30
 * days later unless somebody puts it back — so a wrong click costs a month-long undo, not a
 * consultant's passport.
 *
 * The server decides what may go, and checks again on every click. This screen cannot be talked
 * into removing something in use by being out of date: at worst it offers a button the server
 * then refuses, and the refusal says why.
 */

const STATUS = {
  in_use: { label: 'In use', cls: 'bg-green-100 text-green-800' },
  orphan: { label: 'Not used by anything', cls: 'bg-amber-100 text-amber-900' },
  protected: { label: 'Kept by the system', cls: 'bg-blue-100 text-blue-800' },
  unchecked: { label: 'Not checked', cls: 'bg-gray-200 text-gray-700' },
}

const size = (n) => {
  if (!n) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1)
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`
}

// What is using a file, in words: a record ("users · id_document_url #13"), or a rule the server
// applied ("Give Access copy of user #13's documents").
const usedBy = (u) => u.rule || (
  `${u.table.replace(/_/g, ' ')} · ${u.column}` +
  (u.id != null ? ` #${u.id}` : '') +
  (u.db && u.db !== 'main' ? ` (in ${u.db})` : '')
)

export default function FileManager() {
  const { isDark } = useTheme()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(null)              // key being moved
  const [open, setOpen] = useState(null)              // kind whose files are shown
  const [onlyOrphans, setOnlyOrphans] = useState(false)

  const load = async () => {
    setError(null)
    try {
      const { data } = await api.get('/file-manager/')
      setData(data)
    } catch (err) {
      setError(err.response?.status === 403
        ? 'Only a Super Admin can open the file manager.'
        : err.response?.data?.detail || 'Could not load the files.')
    }
  }

  useEffect(() => { load() }, [])

  const trash = async (file) => {
    if (!window.confirm(
      `Move "${file.path}" to the trash?\n\n` +
      `Nothing uses it. It will be removed for good after ${data.keep_days} days unless you put it back.`)) return
    setBusy(file.key); setNotice(null)
    try {
      const { data: r } = await api.post('/file-manager/trash', { key: file.key })
      setNotice({ kind: 'ok', text: `${file.path} — ${r.detail}` })
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.detail || 'The move failed.' })
    } finally {
      // Reloaded either way: a refusal usually means this page was out of date.
      await load()
      setBusy(null)
    }
  }

  const putBack = async (item) => {
    setBusy(item.key); setNotice(null)
    try {
      const { data: r } = await api.post('/file-manager/restore', { key: item.key })
      setNotice({ kind: 'ok', text: r.detail })
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.detail || 'Could not put it back.' })
    } finally {
      await load()
      setBusy(null)
    }
  }

  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const primary = isDark ? 'text-white' : 'text-gray-900'
  const secondary = isDark ? 'text-gray-400' : 'text-gray-500'
  const divider = isDark ? 'border-gray-700' : 'border-gray-100'

  const groups = (data?.groups || []).filter(g => !onlyOrphans || g.orphan > 0)
  const t = data?.totals

  return (
    <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-5xl mx-auto space-y-5">

        <div>
          <h1 className={`text-2xl font-bold ${primary}`}>File manager</h1>
          <p className={`text-sm mt-1 ${secondary}`}>
            Every stored file, and whether anything still uses it. Only files nothing uses can be
            moved to the trash.
          </p>
        </div>

        {data && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${data.environment === 'production'
            ? (isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-700')
            : (isDark ? 'bg-amber-950/30 border-amber-900 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900')}`}>
            {data.environment === 'production'
              ? <>Showing <strong>production</strong>. Test copies' folders (such as staging/) are left out and cannot be touched from here.</>
              : <>Showing <strong>{data.environment}</strong> — only files under <code>{data.scope}</code>. Production's files cannot be touched from here.</>}
          </div>
        )}

        {/* Said before anything else on the page, because it changes what every badge below means. */}
        {data && !data.complete && (
          <div className="rounded-xl border px-4 py-3 text-sm bg-red-50 border-red-200 text-red-800">
            <strong>Moving to the trash is switched off.</strong> Some records could not be checked,
            so a file that looks unused might not be:
            <ul className="list-disc ml-5 mt-1">
              {data.problems.slice(0, 3).map(p => <li key={p} className="break-all">{p}</li>)}
            </ul>
            {data.problems.length > 3 && <p className="mt-1">…and {data.problems.length - 3} more.</p>}
          </div>
        )}

        {notice && (
          <div className={`rounded-xl border px-4 py-3 text-sm wrap-break-word ${notice.kind === 'error'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-green-50 border-green-200 text-green-800'}`}>
            {notice.text}
          </div>
        )}

        {error && (
          <div className={`rounded-xl border p-6 ${card}`}>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!data && !error && <p className={`text-sm ${secondary}`}>Reading the bucket and checking every record…</p>}

        {data && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className={`text-sm ${secondary}`}>
                {t.count} file{t.count === 1 ? '' : 's'} · {size(t.size)} · {t.in_use} in use ·{' '}
                <span className={t.orphan ? 'text-amber-600 font-medium' : ''}>{t.orphan} not used by anything</span>
                {t.protected > 0 && ` · ${t.protected} kept by the system`}
                {t.unchecked > 0 && ` · ${t.unchecked} not checked`}
              </p>
              <label className={`flex items-center gap-2 text-sm ${secondary}`}>
                <input
                  type="checkbox"
                  checked={onlyOrphans}
                  onChange={(e) => setOnlyOrphans(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Only show files nothing uses
              </label>
            </div>

            {groups.length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${card}`}>
                <p className={`text-sm ${secondary}`}>
                  {onlyOrphans ? 'Every file is in use. Nothing to clear.' : 'The bucket is empty.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map(g => (
                  <div key={g.kind} className={`rounded-xl border ${card}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(open === g.kind ? null : g.kind)}
                      className="w-full p-4 flex items-center gap-3 text-left"
                    >
                      <span className={`text-base w-4 ${secondary}`}>{open === g.kind ? '▾' : '▸'}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium ${primary}`}>{g.label}</p>
                        <p className={`text-xs mt-0.5 ${secondary}`}>
                          {g.kind}/ · {g.count} file{g.count === 1 ? '' : 's'} · {size(g.size)}
                        </p>
                      </div>
                      {g.orphan > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS.orphan.cls}`}>
                          {g.orphan} not used
                        </span>
                      )}
                      {g.unchecked > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS.unchecked.cls}`}>
                          {g.unchecked} not checked
                        </span>
                      )}
                    </button>

                    {open === g.kind && (
                      <div className={`border-t ${divider}`}>
                        {g.files.filter(f => !onlyOrphans || f.status === 'orphan').map(f => (
                          <div key={f.key} className={`px-4 py-3 flex items-start gap-3 border-b last:border-b-0 ${divider}`}>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm break-all ${primary}`}>{f.path}</p>
                              <p className={`text-xs mt-0.5 ${secondary}`}>
                                {size(f.size)} · stored {formatDateTime(f.last_modified)}
                              </p>
                              {f.used_by.length > 0 && (
                                <p className={`text-xs mt-1 ${secondary}`}>
                                  {f.status === 'protected' ? '' : 'Used by: '}
                                  {f.used_by.slice(0, 2).map(usedBy).join('; ')}
                                  {f.used_by.length > 2 && ` and ${f.used_by.length - 2} more`}
                                </p>
                              )}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STATUS[f.status].cls}`}>
                              {STATUS[f.status].label}
                            </span>
                            {f.status === 'orphan' && (
                              <button
                                type="button"
                                onClick={() => trash(f)}
                                disabled={busy !== null}
                                className="px-3 py-1.5 rounded-lg text-xs text-red-600 hover:bg-red-50 disabled:opacity-40 shrink-0"
                              >
                                {busy === f.key ? 'Moving…' : 'Move to trash'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-2">
              <h2 className={`text-lg font-semibold ${primary}`}>Trash</h2>
              <p className={`text-sm mt-0.5 ${secondary}`}>
                Removed for good {data.keep_days} days after being moved here. Until then, putting
                a file back returns it exactly where it was.
              </p>
            </div>

            {data.trash.length === 0 ? (
              <div className={`rounded-xl border p-6 text-center ${card}`}>
                <p className={`text-sm ${secondary}`}>The trash is empty.</p>
              </div>
            ) : (
              <div className={`rounded-xl border ${card}`}>
                {data.trash.map(item => (
                  <div key={item.key} className={`px-4 py-3 flex items-start gap-3 border-b last:border-b-0 ${divider}`}>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm break-all ${primary}`}>{item.path}</p>
                      <p className={`text-xs mt-0.5 ${secondary}`}>
                        {size(item.size)} · moved here {formatDateTime(item.trashed_at)} · removed for good on{' '}
                        {formatDate(item.removes_on)}
                        {' '}({item.days_left} day{item.days_left === 1 ? '' : 's'} left)
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => putBack(item)}
                      disabled={busy !== null}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-40 shrink-0"
                    >
                      {busy === item.key ? 'Working…' : 'Put back'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
